/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, { useEffect, useState, memo, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import { useFormik } from 'formik';
import cn from 'classnames';
import moment from 'moment';
import * as Yup from 'yup';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ActionText from '../../components/ActionText/ActionText';
import ActionIcon from '../../components/ActionIcon/ActionIcon';
import ValidationErrorList from '../../components/ValidationErrorList/ValidationErrorList';
import Button from '../../components/Button/Button';
import SelectSearch from '../../components/SelectSearch';
import InviteToResourceModal from '../../components/InviteToResourceModal/InviteToResourceModal';
import Input from '../../components/Input/Input';
import { getAccessUsersRequest } from '../../store/users/actions';
import Select from '../../components/Select/Select';
import SelectMulti from '../../components/SelectMulti/SelectMulti';
import { roleMapper, capitalize } from '../../helpers/utils';
import {
  searchEndpointsRequest,
  listEndpointsRequest,
} from '../../store/endpoints/actions';

import {
  activeChartRequest,
  modifyChartRequest,
  fetchTestChartDataRequest,
} from '../../store/activeChart/actions';

import {
  searchConnectionsRequest,
  listConnectionsRequest,
} from '../../store/connections/actions';

import {
  searchAuthKeysRequest,
  listAuthKeysRequest,
} from '../../store/authKeys/actions';

import { getConnectedEndpointsToAuthKeysRequest } from '../../store/activeAuthKey/actions';
import { getConnectedEndpointsRequest } from '../../store/activeConnection/actions';


import ConnectedChart, { calculateNumberOfPoints } from '../../containers/ConnectedChart/ConnectedChart';

import { createVariableRequest } from '../../store/variables/actions';
import { createToastRequest } from '../../store/toasts/actions';

import * as shared from '../../styles/shared.module.scss';

const getQueryParams = (search) => new URLSearchParams(search);

const maxAllowedLength = 64;

const labelMatcher = {
  '1 Day': '1d',
  '1 Week': '1w',
  '2 Weeks': '2w',
  '1 Month': '1M',
};

const endUnix = moment().endOf('day');
const startUnix = moment().subtract(2, 'weeks').startOf('day');


const MemoizedConnectedChart = memo(ConnectedChart);

const SUBROUTING = {
  ACCESS: 'access',
};

const metricTypeMapper = {
  'api': {
    label: 'API',
    title: 'Create new API',
    url: '/endpoints/create',
  },
  "auth-key-with-api": {
    label: 'Auth key',
    title: 'Create new auth key',
    url: '/auth-keys/create',
  },
  'connection-with-api': {
    label: 'Connection',
    title: 'Create new connection',
    url: '/connections/create',
  },
  'connection': {
    label: 'Connection',
    title: 'Create new connection',
    url: '/connections/create',
  },
  'auth-key': {
    label: 'Auth key',
    title: 'Create new auth key',
    url: '/auth-keys/create',
  },
}

const metricTypes = {
  "api": "API",
  "connection": "Connection",
  "auth-key": "Auth key",
  "auth-key-with-api": "Auth key with API",
  "connection-with-api": "Connection with API",
}

const metricPlaceholders = {
  'api': 'Search API to get metrics',
  'connection': 'Search connection to get metrics',
  'auth-key': 'Search auth key to get metrics',
  'auth-key-with-api': 'Search auth key to get metrics',
  'connection-with-api': 'Search connection to get metrics',
};

const refRates = [
  { value: '30s', title: '30 seconds' },
  { value: '1m', title: '1 minute' },
  { value: '3m', title: '3 minutes' },
  { value: '5m', title: '5 minutes' },
  { value: '10m', title: '10 minutes' },
  { value: '15m', title: '15 minutes' },
  { value: '30m', title: '30 minutes' },
  { value: '1h', title: '1 hour' },
]

const subrouteMapping = {};

const AuthSchema = () =>
  Yup.object().shape({
    name: Yup.string()
      .min(2, 'Name length should be at least 2 characters')
      .max(64)
      .required('Name required'),
    metricsType: Yup.string().required('Type required'),
    connectedModel: Yup.string().required('Connected model required'),
    connectedSubModel: Yup.string().when('metricsType', {
      is: (metricsType) => metricsType === 'auth-key-with-api' || metricsType === 'connection-with-api',
      then: Yup.string().required('Submodel required'),
    }),
    chartType: Yup.string().required('Chart type required'),
    dataGroups: Yup.array()
      .of(Yup.string().required('Each data group must be a string'))
      .min(1, 'Data groups required')
      .required('Data groups required'),
    refresh: Yup.string().required('Refresh rate required'),
  });

function DashboardsModify({
  currentUser,
  activeProject,
  HTTPCode,
  chartType,
  entityType,

  getEndpoints,
  getConnections,
  getAuthKeys,

  getConnectedEndpointsToConnection,
  getConnectedEndpointsToAuthKeys,

  getActiveChart,
  activeChart,
  modifyActiveChart,
  fetchChartTestData,
  testChartData,
}) {
  const scrollRef = useRef(null);
  const ref = useRef({ 
    dashboard: 
    { range: `${startUnix.format('HH:mm DD/MM/YY')} - ${endUnix.format('HH:mm DD/MM/YY')}`, startDate: startUnix, endDate: endUnix 
  } });
  const [subroute, setSubroute] = useState(SUBROUTING.ACCESS);
  const [shareModalOpened, setShareModalOpened] = useState(false);
  const [inviteList, setInviteList] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { shortName, resourceId } = useParams();
  const listAPI = () => navigate(`/${shortName}/dashboards`);
  const [submitTriggered, setSubmitTriggered] = useState(false);
  const [isChange, setIsChange] = useState(false);

  const [connectedModel, setConnectedModel] = useState({ type: 'api', value: [] });
  const [connectedSubModel, setConnectedSubModel] = useState({ type: 'api-auth-key', value: [] });

  const normalizedConnectedModel = connectedModel.value.map((el) => ({ value: String(el.id), title: el.name || el.instanceName }));
  const normalizedConnectedSubmodels = connectedSubModel.value.map((el) => ({ value: String(el.id), title: el.name || el.instanceName }));

  const HTTPCodeList = HTTPCode ? HTTPCode.map((el) => ({ value: el, title: el })) : [];
  const chartTypeList = chartType ? chartType.map((el) => ({ value: el, title: capitalize(el) })) : [];
  const metricTypeList = entityType ? entityType.map((el) => ({ value: el, title: metricTypes[el] || el })) : [];

  useEffect(() => {
    if (currentUser) {
      setInviteList([
        {
          userFirstName: currentUser.firstName,
          userLastName: currentUser.lastName,
          userEmail: currentUser.email,
          roleId: 2,
        },
      ]);
    }
  }, [currentUser]);

  useEffect(() => {
    getActiveChart({ id: resourceId });
  }, [resourceId]);

  const {
    submitForm,
    handleChange: handleChange2,
    setFieldValue: setFieldValue2,
    values,
    errors,
    validateForm,
    setValues,
  } = useFormik({
    initialValues: {
      name: '',
      description: '',
      type: 'string',
      value: '',
      chartType: 'bar',
      refresh: '1h',
      metricsType: 'api',
      dataGroups: [
        "2XX",
        "3XX",
        "4XX",
        "5XX"
      ],
    },
    validationSchema: AuthSchema(),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (v) => {
      const req = {
        name: v.name,
        description: v.description,
        projectId: activeProject?.id,
        entityType: v.metricsType,
        entityId: Number(v.connectedModel) || undefined,
        linkedEntityId: (
          v.metricsType === 'auth-key-with-api'
          || v.metricsType === 'connection-with-api') ? Number(v.connectedSubModel) : undefined,
        type: v.chartType,
        group: v.dataGroups,
        refresh: v.refresh,
        status: 'active',
        id: activeChart?.id,
      };
      modifyActiveChart(req, () => {
        setIsChange(false);
      });
    },
  });

  useEffect(() => {
    const queryParams = getQueryParams(location.search);
    if (activeChart) {
      const {
        name,
        description,
        entityType: metricsType,
        entityId: connectedModel,
        linkedEntityId: connectedSubModel,
        group: dataGroups,
      } = activeChart || {};

      const baseParam = {
        name,
        description,
        metricsType,
        connectedModel: String(connectedModel),
        connectedSubModel: connectedSubModel ? String(connectedSubModel) : '',
        chartType: queryParams.get('type') || 'bar',
        dataGroups,
        refresh: queryParams.get('refresh') || '1h',
        interval: '1d',
      }

      setValues({
        ...baseParam,
      });
    }
  }, [activeChart]);

  const debouncedValidateForm = debounce(() => {
    validateForm();
  }, 100);

  const setFieldValue = (...args) => {
    setIsChange(true);
    setFieldValue2(...args);
    if (submitTriggered) {
      debouncedValidateForm();
    }
  };

  const handleChange = (...args) => {
    setIsChange(true);
    handleChange2(...args);
    if (submitTriggered) {
      debouncedValidateForm();
    }
  };

  const handleSubmitWithCallback = (e) => {
    e.preventDefault();
    setSubmitTriggered(true);
    submitForm(e);
  };

  const handleChangeProxy = (...arg) => handleChange(...arg);

  const setFieldValueProxy = (...arg) => setFieldValue(...arg);

  const handeChangeRole = (email, role) => {
    const newInviteList = inviteList.map((el) => (el.userEmail === email ? { ...el, roleId: role } : el));
    setInviteList(newInviteList);
  };

  const saveInviteList = (data) => {
    const filtered = data.accessList.filter((el) => el.userEmail !== currentUser.email);
    const unique = Object.values(
      [...inviteList, ...filtered].reduce((acc, obj) => ({ ...acc, [obj.userEmail]: obj }), {}),
    );
    setInviteList(unique);
    setShareModalOpened(false);
  };

  const refreshModels = () => {
    if (values.metricsType === 'api') {
      getEndpoints({ id: activeProject?.id }, (response) => {
        setConnectedModel({ type: 'api', value: response });
      });
    } else if (values.metricsType === 'connection' || values.metricsType === 'connection-with-api') {
      getConnections({ id: activeProject?.id }, (response) => {
        setConnectedModel({ type: 'connection', value: response });
      });
    } else if (values.metricsType === 'auth-key' || values.metricsType === 'auth-key-with-api') {
      getAuthKeys({ id: activeProject?.id }, (response) => {
        setConnectedModel({ type: 'auth-key', value: response });
      });
    }
  };

  const refreshSubModels = () => {
    if (values.metricsType === 'auth-key-with-api' && values.connectedModel && connectedModel?.type === 'auth-key') {
      getConnectedEndpointsToAuthKeys({ id: values.connectedModel }, (response) => {
        setConnectedSubModel({ type: 'api-auth-key', value: response });
      });
    }
    if (values.metricsType === 'connection-with-api' && values.connectedModel && connectedModel?.type === 'connection') {
      getConnectedEndpointsToConnection({ id: values.connectedModel }, (response) => {
        setConnectedSubModel({ type: 'api-connection', value: response });
      });
    }
  };

  useEffect(() => {
    if (!activeProject) return;
    refreshModels();
  }, [values.metricsType, activeProject]);

  useEffect(() => {
    refreshSubModels();
  }, [values.metricsType, values.connectedModel, connectedModel]);

  useEffect(() => {
    if (values.connectedModel && (['api', 'connection', 'auth-key'].includes(values.metricsType)) && values.dataGroups) {
      const { range, startDate, endDate } = ref.current.dashboard || {};
      const newRange = labelMatcher[range] || `${startDate.unix()}:${endDate.unix()}`;
      const offset = startDate.utcOffset();

      fetchChartTestData({
        range: newRange,
        interval: values.interval,
        group: values.dataGroups,
        entityType: values.metricsType,
        entityId: Number(values.connectedModel),
        linkedEntityId: values.connectedSubModel,
        timezoneOffset: offset > 0 ? `+${offset}` : `-${offset}`,
      });
    }

  }, [values.connectedModel, values.dataGroups]);

  useEffect(() => {
    if (values.connectedModel && (['auth-key-with-api', 'connection-with-api'].includes(values.metricsType)) && values.connectedSubModel && values.dataGroups) {
      const { range, startDate, endDate } = ref.current.dashboard || {};
      const newRange = labelMatcher[range] || `${startDate.unix()}:${endDate.unix()}`;
      const offset = startDate.utcOffset();

      fetchChartTestData({
        range: newRange,
        interval: values.interval,
        group: values.dataGroups,
        entityType: values.metricsType,
        entityId: Number(values.connectedModel),
        linkedEntityId: Number(values.connectedSubModel),
        timezoneOffset: offset > 0 ? `+${offset}` : `-${offset}`,
      });
    }
  }, [values.connectedModel, values.connectedSubModel, values.dataGroups]);

  const handleChangeName = ({ target }) => {
    const regex = /^(?!\s)(?!.* {2})/;
    setIsChange(true);
    if (regex.test(target.value) && target.value.length <= maxAllowedLength) {
      setFieldValue(target.name, target.value);
    }
  };

  const changeRange = (id, range, startDate, endDate) => {
    if (!ref.current[id]) {
      ref.current[id] = { range, startDate, endDate };
    } else if (ref.current[id] && ref.current[id]?.range !== range &&
      (values.connectedModel && ['api', 'connection', 'auth-key'].includes(values.metricsType) || 
      values.connectedModel && values.connectedSubModel && ['auth-key-with-api', 'connection-with-api'].includes(values.metricsType))) {
      ref.current[id] = { range, startDate, endDate }; 
      const newRange = labelMatcher[range] || `${startDate.unix()}:${endDate.unix()}`;
      const offset = startDate.utcOffset();

      let interval = values?.interval;

      if (values?.interval === '1h' || values?.interval === '1m') {
        const { durationInMinutes, durationInHours } = calculateNumberOfPoints(newRange);
  
          // First check if the number of points in minutes exceeds the threshold
          if (durationInMinutes > 744) {
            interval = '1h';
            setFieldValueProxy('interval', '1h');
          }

          // Then check if the number of points in hours exceeds the threshold
          if (durationInHours > 744) {
            interval = '1d';
            setFieldValueProxy('interval', '1d');
          }
      }
  
      fetchChartTestData({
        range: newRange,
        interval,
        group: values.dataGroups,
        entityType: values.metricsType,
        entityId: values.connectedModel,
        linkedEntityId: values.connectedSubModel,
        timezoneOffset: offset > 0 ? `+${offset}` : `-${offset}`,
      });
    } else if (ref.current[id]) {
      ref.current[id] = { range, startDate, endDate }; 
      const newRange = labelMatcher[range] || `${startDate.unix()}:${endDate.unix()}`;

      if (values?.interval === '1h' || values?.interval === '1m') {
        const { durationInMinutes, durationInHours } = calculateNumberOfPoints(newRange);
  
          // First check if the number of points in minutes exceeds the threshold
          if (durationInMinutes > 744) {
            setFieldValueProxy('interval', '1h');
          }

          // Then check if the number of points in hours exceeds the threshold
          if (durationInHours > 744) {
            setFieldValueProxy('interval', '1d');
          }
      }
    }
  };

  const onCLickChartOption = (key, option) => {
    if (key === 'refresh') {
      setFieldValueProxy('refresh', option);
    } else if (key === 'chartType') {
      setFieldValueProxy('chartType', option);
    } else if (key === 'interval') {
      setFieldValueProxy('interval', option);
      if (ref.current.dashboard &&
        (values.connectedModel && ['api', 'connection', 'auth-key'].includes(values.metricsType) || 
        values.connectedModel && values.connectedSubModel && ['auth-key-with-api', 'connection-with-api'].includes(values.metricsType))) {
        const  { range, startDate, endDate } = ref.current.dashboard || {};
        const newRange = labelMatcher[range] || `${startDate.unix()}:${endDate.unix()}`;
        const offset = startDate.utcOffset();
    
        fetchChartTestData({
          range: newRange,
          interval: option,
          group: values.dataGroups,
          entityType: values.metricsType,
          entityId: values.connectedModel,
          linkedEntityId: values.connectedSubModel,
          timezoneOffset: offset > 0 ? `+${offset}` : `-${offset}`,
        });
      }
    }
  };

  const chartData = useMemo(() => {
    const isMetricsTypeValid = ['auth-key-with-api', 'connection-with-api', 'api', 'connection', 'auth-key'].includes(values.metricsType);
    const isConnectedModelValid = (['auth-key-with-api', 'connection-with-api'].includes(values.metricsType) && values.connectedSubModel) || 
                                  (['api', 'connection', 'auth-key'].includes(values.metricsType) && values.connectedModel);
    const hasDataGroups = values.dataGroups?.length > 0;
  
    return (isMetricsTypeValid && isConnectedModelValid && hasDataGroups) ? testChartData : [];
  }, [values.metricsType, values.connectedSubModel, values.connectedModel, values.dataGroups, testChartData]);

  return (
    <div className={shared.page}>
      <div className={shared.header}>
        <div className={shared.headerTitleGroup}>
          <h1 className={shared.headerTitle}>Modify Chart</h1>
        </div>
        <div className={shared.headerButtonGroup}>
          <ValidationErrorList errors={errors} subrouteMapping={subrouteMapping} setSubroute={setSubroute} />
          <Button title="Cancel" type="secondary" onClick={listAPI} />
          <Button title="Modify chart" onClick={handleSubmitWithCallback} disabled={!isChange} />
        </div>
      </div>
      <div className={shared.body}>
        <div className={shared.mainContent} ref={scrollRef}  style={{ overflowX: 'hidden' }}>
          <section className={shared.section}>
            <h4 className={shared.sectionHeader}>Metrics Settings</h4>
            <div className={shared.sectionContainer}>
              <div className={shared.inputWrapper}>
                <span className={shared.inputLabel}>Name</span>
                <Input
                  placeholder="Enter name"
                  handleChange={handleChangeName}
                  autoComplete="off"
                  value={values.name}
                  name="name"
                  id="name"
                  error={!!errors.name}
                />
              </div>
              <div className={shared.inputWrapper}>
                <span className={shared.inputLabel}>
                  Description <span className={shared.inputLabelDetails}> (optional)</span>
                </span>
                <Input
                  placeholder="Enter description"
                  handleChange={handleChangeProxy}
                  value={values.description}
                  name="description"
                  id="description"
                />
              </div>

              <div className={shared.inputWrapper}>
                <span className={shared.inputLabel}>Metrics type</span>
                <Select
                  list={metricTypeList}
                  value={values.metricsType}
                  handleChange={(...e) => {
                    setFieldValueProxy(...e);
                    setFieldValueProxy('connectedModel', '');
                  }}
                  placeholder="Select type"
                  name="metricsType"
                  id="metricsType"
                  error={!!errors.metricsType}
                />
              </div>

              <div className={shared.inputWrapper}>
                <span className={shared.inputLabel}>{metricTypeMapper[values.metricsType]?.label}</span>
                <SelectSearch
                  list={[
                    ...normalizedConnectedModel,
                    ...(values.metricsType ? [{
                      value: 'new',
                      title: 'New',
                      component: (
                        <ActionText
                          title={metricTypeMapper[values.metricsType]?.title || 'Model create'}
                          target={`/${shortName}${metricTypeMapper[values.metricsType]?.url || ''}`}
                        />
                      ),
                    }] : [])
                  ]}
                  connectedField={values.metricsType}
                  iconRight="search-2"
                  value={values.connectedModel}
                  handleChange={(...e) => {
                    setFieldValueProxy(...e);
                  }}
                  placeholder={metricPlaceholders[values.metricsType] || 'Search entity to get metrics'}
                  name="connectedModel"
                  id="connectedModel"
                  error={!!errors.connectedModel}
                />
              </div>
              {
                ((values.metricsType === 'auth-key-with-api' || values?.metricsType === 'connection-with-api')) ? (
                  <div className={shared.inputWrapper}>
                    <span className={shared.inputLabel}>API</span>
                    <SelectSearch
                      list={
                        [
                          ...normalizedConnectedSubmodels,
                          ...(values.metricsType ? [{
                            value: 'new',
                            title: 'New',
                            component: (
                              <ActionText
                                title="Create new API"
                                target={`/${shortName}/endpoints/create`}
                              />
                            ),
                          }] : [])
                        ]
                      }
                      value={values.connectedSubModel}
                      handleChange={(...e) => {
                        setFieldValueProxy(...e);
                      }}
                      placeholder="Search for API"
                      name="connectedSubModel"
                      id="connectedSubModel"
                      error={!!errors.connectedSubModel}
                    />
                  </div>
                ) : null
              }
            </div>
          </section>

          <hr />

          <section className={shared.section}>
            <h4 className={shared.sectionHeader}>Chart Settings</h4>
            <div className={shared.sectionContainer}>
            <div className={shared.inputWrapper}>
                <span className={shared.inputLabel}>Chart type</span>
                <Select
                  list={chartTypeList}
                  value={values.chartType}
                  handleChange={(...e) => {
                    setFieldValueProxy(...e);
                  }}
                  placeholder="Select type"
                  name="chartType"
                  id="chartType"
                  error={!!errors.chartType}
                />
              </div>

              <div className={shared.inputWrapper}>
                <span className={shared.inputLabel}>
                  <span>Chart data groups</span>
                  <ActionText
                    title={
                      <ActionIcon
                        id="s3Method_tooltip"
                        icon="info"
                        style={{ marginLeft: '8px' }}
                        tooltip={{
                          children: (
                            <div style={{ textAlign: 'left' }}>Filter result with groups</div>
                          ),
                          place: 'right',
                          openOnClick: false,
                          clickable: true,
                        }}
                      />
                    }
                  />
                </span>
                <SelectMulti
                  list={HTTPCodeList}
                  value={values.dataGroups}
                  handleChange={(...e) => {
                    setFieldValueProxy(...e);
                  }}
                  placeholder="Select data groups"
                  name="dataGroups"
                  id="dataGroups"
                  error={!!errors.dataGroups}
                />
              </div>

              <div className={shared.inputWrapper}>
                <span className={shared.inputLabel}>Refresh rate</span>
                <Select
                  list={refRates}
                  value={values.refresh}
                  handleChange={(...e) => {
                    setFieldValueProxy(...e);
                  }}
                  placeholder="Select refresh rate"
                  name="refresh"
                  id="refresh"
                  error={!!errors.refresh}
                />
              </div>
            </div>
          </section>

          {values.chartType && values.dataGroups ? (
            <div style={{
                height: '21px',
                borderBottom: '1px solid rgba(232, 233, 237, 1)',
                width: 'calc(100% + 40px)',
                transform: 'translateX(-20px)',
              }}
            />
          ) : null}

          {values.chartType && values.dataGroups ? (
            <section className={shared.section}>
              <h4 className={shared.sectionHeader} style={{ marginTop: '20px' }}>Chart Preview</h4>
              <div className={cn(shared.sectionContainer, shared.wide)}>
              <DndProvider backend={HTML5Backend}>
                <MemoizedConnectedChart
                    chartType={values.chartType}
                    groups={values.dataGroups}
                    refresh={values.refresh}
                    title={values.name}
                    interval={values.interval}
                    description={values.description}
                    handleClickSelectOption={onCLickChartOption}
                    triggerSaveRange={changeRange}
                    initValue={`${startUnix.unix()}:${endUnix.unix()}`}
                    model={{ data: chartData }}
                    scrollRef={scrollRef}
                    id="dashboard"
                />
              </DndProvider>
              </div>
            </section>
          ) : null}

        <section style={{marginTop: 60 }} className={shared.section} />

        </div>
        <div className={shared.sidepanel}>
          <div className={shared.sidepanelGroup}>
            <div className={shared.sidepanelHeading}>
              <div className={shared.subHeaders}>
                <h4
                  onClick={() => setSubroute(SUBROUTING.ACCESS)}
                  className={cn(
                    shared.sectionHeader,
                    { [shared.clickable]: true },
                    { [shared.passive]: subroute !== SUBROUTING.ACCESS },
                  )}
                >
                  Access
                </h4>
              </div>
            </div>
            {subroute === SUBROUTING.ACCESS && (
              <div className={shared.sidepanelTable}>
                <div className={shared.sidepanelTableHeading}>
                  <div className={shared.lineHeader}>
                    <div>Name</div>
                    <div />
                    <div>Role</div>
                  </div>
                </div>
                <div className={shared.sidepanelTableBodyContainer}>
                  <ul className={shared.sidepanelTableBody}>
                    {inviteList &&
                      inviteList.map((item) => (
                        <li className={shared.historyLine} key={item.userEmail}>
                          <div title={item.userEmail}>
                            {item.userEmail === currentUser.email ? (
                              <span>{`${item.userFirstName} ${item.userLastName}`}</span>
                            ) : (
                              <span>{item.userEmail}</span>
                            )}
                          </div>
                          <div />
                          <div>
                            {item.userEmail === currentUser.email ? (
                              <Select
                                list={[{ value: 2, title: roleMapper(2) }]}
                                style={{ width: 90 }}
                                value={item.roleId}
                                handleChange={(email, value) => handeChangeRole(email, value)}
                                name={item.userEmail}
                                disabled
                              />
                            ) : (
                              <Select
                                list={[
                                  { value: 3, title: roleMapper(3) },
                                  { value: 4, title: roleMapper(4) },
                                ]}
                                style={{ width: 90 }}
                                value={item.roleId}
                                handleChange={(email, value) => handeChangeRole(email, value)}
                                name={item.userEmail}
                              />
                            )}
                          </div>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
        {shareModalOpened && (
          <InviteToResourceModal onClose={() => setShareModalOpened(false)} onSubmit={saveInviteList} model={values} />
        )}
      </div>
    </div>
  );
}

const mapStateToProps = ({ auth: { currentUser }, activeChart, activeProject, directory }) => ({
  activeProject,
  currentUser,
  testChartData: activeChart?.testChartData,
  activeChart: activeChart?.activeChart,
  HTTPCode: directory?.HTTPCode,
  chartType: directory?.chartType,
  entityType: directory?.entityType,
});

const mapDispatchToProps = (dispatch) => ({
  getAccessUsers: (data) => dispatch(getAccessUsersRequest(data)),
  createVariable: (data, callback) => dispatch(createVariableRequest(data, callback)),
  createToast: (data, callback) => dispatch(createToastRequest(data, callback)),

  searchEndpoints: (data, callback) => dispatch(searchEndpointsRequest(data, callback)),
  searchConnections: (data, callback) => dispatch(searchConnectionsRequest(data, callback)),
  searchAuthKeys: (data, callback) => dispatch(searchAuthKeysRequest(data, callback)),

  getEndpoints: (data, callback) => dispatch(listEndpointsRequest(data, callback)),
  getConnections: (data, callback) => dispatch(listConnectionsRequest(data, callback)),
  getAuthKeys: (data, callback) => dispatch(listAuthKeysRequest(data, callback)),

  getConnectedEndpointsToConnection: (data, callback) => dispatch(getConnectedEndpointsRequest(data, callback)),
  getConnectedEndpointsToAuthKeys: (data, callback) => dispatch(getConnectedEndpointsToAuthKeysRequest(data, callback)),

  getActiveChart: (data, callback) => dispatch(activeChartRequest(data, callback)),
  modifyActiveChart: (data, callback) => dispatch(modifyChartRequest(data, callback)),
  fetchChartTestData: (data, callback) => dispatch(fetchTestChartDataRequest(data, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DashboardsModify);
